// Shared hooks and utilities for admin pages
import { useState, useEffect, useCallback } from 'react';
import { useGlobal } from '@/context/GlobalContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

// Generic CRUD hook for admin pages
export const useAdminCrud = (endpoint, entityName) => {
  const { API } = useGlobal();
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchItems = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/${endpoint}`);
      setItems(response.data);
    } catch (error) {
      toast.error(`Failed to fetch ${entityName}`);
    } finally {
      setLoading(false);
    }
  }, [API, endpoint, entityName]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const createItem = async (data) => {
    try {
      await axios.post(`${API}/${endpoint}`, data, { headers });
      toast.success(`${entityName} created`);
      setIsDialogOpen(false);
      fetchItems();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
      return false;
    }
  };

  const updateItem = async (id, data) => {
    try {
      await axios.put(`${API}/${endpoint}/${id}`, data, { headers });
      toast.success(`${entityName} updated`);
      setIsDialogOpen(false);
      fetchItems();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
      return false;
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm(`Are you sure you want to delete this ${entityName.toLowerCase()}?`)) return false;
    try {
      await axios.delete(`${API}/${endpoint}/${id}`, { headers });
      toast.success(`${entityName} deleted`);
      fetchItems();
      return true;
    } catch (error) {
      toast.error(`Failed to delete ${entityName.toLowerCase()}`);
      return false;
    }
  };

  const openCreate = () => {
    setEditingItem(null);
    setIsDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingItem(null);
  };

  return {
    items,
    loading,
    isDialogOpen,
    editingItem,
    setIsDialogOpen,
    createItem,
    updateItem,
    deleteItem,
    openCreate,
    openEdit,
    closeDialog,
    refetch: fetchItems
  };
};

// Common form field change handler
export const useFormData = (initialState) => {
  const [formData, setFormData] = useState(initialState);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => setFormData(initialState);

  const setFromItem = (item, fieldMap) => {
    const newData = {};
    Object.keys(fieldMap).forEach(key => {
      newData[key] = item[fieldMap[key]] || fieldMap[key + '_default'] || '';
    });
    setFormData(newData);
  };

  return { formData, setFormData, updateField, resetForm, setFromItem };
};
